import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation } from 'framer-motion';

const ScrollRevealSection = ({ 
  children, 
  id, 
  className = '',
  threshold = 0.3,
  once = true,
  delay = 0,
  duration = 0.5,
  distance = 50,
  direction = 'up', // 'up', 'down', 'left', 'right'
  staggerChildren = 0.1
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, threshold });
  const controls = useAnimation();

  useEffect(() => {
    if (isInView) {
      controls.start('visible');
    } else if (!once) {
      controls.start('hidden');
    }
  }, [isInView, controls, once]);

  // Direction based movement
  const getDirectionOffset = () => {
    switch (direction) {
      case 'up': return { y: distance };
      case 'down': return { y: -distance };
      case 'left': return { x: distance };
      case 'right': return { x: -distance };
      default: return { y: distance };
    }
  };

  // Variants for container
  const containerVariants = {
    hidden: { opacity: 0, ...getDirectionOffset() },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration,
        delay,
        ease: "easeOut",
        staggerChildren: staggerChildren
      }
    }
  };

  // Child variants for staggered animations
  const childVariants = {
    hidden: { opacity: 0, ...getDirectionOffset() },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: { duration, ease: "easeOut" }
    }
  };

  return (
    <motion.div
      id={id}
      ref={ref}
      className={`${className}`}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
    >
      {React.Children.map(children, (child, index) => {
        // If child is a React element, wrap it with motion.div
        if (React.isValidElement(child)) {
          return (
            <motion.div key={index} variants={childVariants}>
              {child}
            </motion.div>
          );
        }
        // Otherwise just return the child
        return child;
      })}
    </motion.div>
  );
};

// Simpler component version for direct use without children mapping
const ScrollReveal = ({ 
  children, 
  threshold = 0.3,
  once = true, 
  delay = 0,
  duration = 0.5,
  distance = 50,
  direction = 'up'
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, threshold });
  
  // Direction based movement
  const getDirectionOffset = () => {
    switch (direction) {
      case 'up': return { y: distance };
      case 'down': return { y: -distance };
      case 'left': return { x: distance };
      case 'right': return { x: -distance };
      default: return { y: distance };
    }
  };
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, ...getDirectionOffset() }}
      animate={isInView ? { opacity: 1, x: 0, y: 0 } : { opacity: 0, ...getDirectionOffset() }}
      transition={{ duration, delay, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

export { ScrollRevealSection, ScrollReveal };